import {
  afterNextRender,
  DOCUMENT,
  EnvironmentInjector,
  inject,
  provideAppInitializer,
} from '@angular/core';
import {
  Data,
  Event,
  NavigationCancel,
  NavigationCancellationCode,
  NavigationEnd,
  NavigationError,
  NavigationSkipped,
  Router,
} from '@angular/router';
import { filter, take } from 'rxjs';
import { AppVersionService } from '../services/app-version.service';

const SPLASH_ID = 'app-splash';
/** Marks the app root that `index.html` makes `inert` while the splash covers it. */
const COVERED_ATTR = 'data-app-splash-covered';
/** Longer than the 300 ms fade in `index.html`. */
const SPLASH_FADE_FALLBACK_MS = 500;
/** How long a pending reload may keep the splash before the page underneath is shown anyway. */
const SPLASH_RELOAD_GRACE_MS = 10_000;

/**
 * Route `data` key for a page with nothing to show that moves on by itself — the auth callback
 * exchanging its token for the cabinet one. The splash stays over it until the next page renders.
 */
export const HOLD_APP_SPLASH = 'holdAppSplash';

/** The part of `ActivatedRouteSnapshot` the splash reads. */
interface SplashRouteNode {
  readonly data: Data;
  readonly firstChild: SplashRouteNode | null;
}

const holdsAppSplash = (route: SplashRouteNode | null): boolean =>
  !!route && (route.data[HOLD_APP_SPLASH] === true || holdsAppSplash(route.firstChild));

/**
 * Fades out the boot splash from `index.html`, drops it from the DOM and hands keyboard and
 * screen-reader access back to the app it covered.
 */
export function removeAppSplash(document: Document = globalThis.document): void {
  document.querySelectorAll(`[${COVERED_ATTR}]`).forEach((root) => {
    root.removeAttribute('inert');
    root.removeAttribute(COVERED_ATTR);
  });

  const splash = document.getElementById(SPLASH_ID);
  if (!splash) return;
  splash.addEventListener('transitionend', () => splash.remove(), { once: true });
  // A tab booted in the background may never run the fade, and no `transitionend` would follow.
  setTimeout(() => splash.remove(), SPLASH_FADE_FALLBACK_MS);
  splash.classList.add('app-splash--hidden');
}

/**
 * A guard redirect or a superseding navigation cancels the first navigation while the next one
 * is already on its way, so the splash stays until a navigation actually settles. A navigation
 * that ends on a `HOLD_APP_SPLASH` route keeps it for the next one; one that fails or is rejected
 * on its way out still releases it, so the splash never outlives a page that stopped moving.
 */
export const settlesInitialNavigation = (
  event: Event,
  activeRoot: () => SplashRouteNode,
): boolean =>
  event instanceof NavigationEnd
    ? !holdsAppSplash(activeRoot())
    : event instanceof NavigationError ||
      event instanceof NavigationSkipped ||
      (event instanceof NavigationCancel &&
        event.code !== NavigationCancellationCode.Redirect &&
        event.code !== NavigationCancellationCode.SupersededByNewNavigation);

/**
 * Keeps the boot splash over the app until the first route has rendered — through the app
 * initializers, the auth guards and the lazy route chunk — instead of revealing an empty shell.
 * Provided only by the apps whose `index.html` carries the splash.
 */
export const provideAppSplash = () =>
  provideAppInitializer(() => {
    const document = inject(DOCUMENT);
    const injector = inject(EnvironmentInjector);
    const appVersionService = inject(AppVersionService);
    const router = inject(Router);
    router.events
      .pipe(
        filter((event) => settlesInitialNavigation(event, () => router.routerState.snapshot.root)),
        take(1),
      )
      .subscribe(() =>
        afterNextRender(
          () => {
            // A failed chunk on the first navigation reloads the page: keep the splash while it
            // unloads, but not forever, in case the page never does.
            if (appVersionService.isReloading()) {
              setTimeout(() => removeAppSplash(document), SPLASH_RELOAD_GRACE_MS);
            } else {
              removeAppSplash(document);
            }
          },
          { injector },
        ),
      );
  });
